import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Select, Store } from "@ngxs/store";
import { MenuItems } from "app/shared/menu-items/menu-items";
import { Observable, Subject } from "rxjs";
import { map, takeUntil, tap } from "rxjs/operators";
import { MemberStatusState } from "~app/addresses/store/member-status";
import {
    ExerciseStatus,
    GlobalMemberStatus,
} from "~app/addresses/store/member-status/member-status.model";
import { AuthUser } from "~app/core/auth/core/auth-user.model";
import { AuthState } from "~app/core/auth/core/auth.state";
import { SubscriptionState } from "~app/farp-odq/store/subscription";
import {
    SeeParticularConditions,
    SeeSubscriptionReceipt,
} from "~app/farp-odq/store/subscription/subscription.actions";
import {
    MemberInsuranceViewModel,
    SubscriptionPeriod,
    SubscriptionReceipt,
} from "~app/farp-odq/store/subscription/subscription.model";

@Component({
    selector: "odq-home-farp-odq",
    templateUrl: "./home-farp-odq.component.html",
    styleUrls: ["./home-farp-odq.component.scss"],
    standalone: false
})
export class HomeFarpOdqComponent implements OnInit, OnDestroy {
  isLoading = false;
  destroy$ = new Subject<void>();
  isInscriptionStatus: boolean = null;
  isExerciseStatus: boolean = null;
  isExemptFromInsurance: boolean = null;
  isWaitingForInsurance: boolean = null;
  hasSubscriptionFormEditable: boolean = null;
  isAuthorized: boolean;

  userCanEditForm$ = this.store.select<boolean>(
    SubscriptionState.userCanEditForm
  );
  userCanEditForm: boolean = null;

  @Select(MemberStatusState.currentMemberStatus)
  currentMemberStatus$: Observable<GlobalMemberStatus>;
  currentInsuranceStatus$: Observable<ExerciseStatus>;

  @Select(SubscriptionState.souscriptionStatus)
  memberStatus$: Observable<MemberInsuranceViewModel[]>;
  memberStatus: GlobalMemberStatus;

  @Select(SubscriptionState.loadingStatus)
  loadingStatus$: Observable<boolean>;
  loadingStatus: boolean;

  periods$ = this.store.select<SubscriptionPeriod[]>(SubscriptionState.periods);
  loadingPeriods$ = this.store.select<boolean>(
    SubscriptionState.loadingPeriods
  );
  loadingReport$ = this.store.select<boolean>(SubscriptionState.loadingReport);
  subscriptionPeriods: SubscriptionPeriod[];

  @Select(AuthState.signedIn) signedIn$: Observable<boolean>;
  user: AuthUser;
  memberId$ = this.store.select<string>(SubscriptionState.memberId);
  memberId: string;

  @Select(AuthState.inactiveMemberForSubscription)
  inactiveMemberForSubscription$: Observable<boolean>;
  inactiveMemberForSubscription: boolean;

  @Select(AuthState.user) user$: Observable<AuthUser>;

  constructor(
    public translate: TranslateService,
    private store: Store,
    public menuItems: MenuItems,
    private router: Router
  ) { }

  ngOnInit() {
    this.isLoading = true;
    this.hasSubscriptionFormEditable = false;

    this.user$.pipe(
      takeUntil(this.destroy$),
      tap(user => {
        this.user = user;
      })
    ).subscribe();

    this.memberId$
      .pipe(
        takeUntil(this.destroy$),
        tap((m) => {
          if (m) {
            this.memberId = m;
          }
        })
      )
      .subscribe();

    this.inactiveMemberForSubscription$
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.inactiveMemberForSubscription = value;
        this.redirectToSubscriptionPageIfNeeded();
      });

    this.signedIn$.pipe(takeUntil(this.destroy$)).subscribe((isAuthorized) => {
      this.isAuthorized = isAuthorized;
      this.redirectToSubscriptionPageIfNeeded();
    });

    this.currentInsuranceStatus$ = this.currentMemberStatus$.pipe(
      map((gbSt) => gbSt?.effectiveInsuranceStatus)
    );

    this.memberStatus$
      .pipe(
        map((memberStatus: MemberInsuranceViewModel[]) => {
          if (!!memberStatus) {
            this.isInscriptionStatus = memberStatus[0]?.isActiveInscription;
            this.isExerciseStatus = memberStatus[0]?.isActiveExercise;
            this.isExemptFromInsurance = memberStatus[0]?.isExemptFromInsurance;
            this.isWaitingForInsurance = memberStatus[0]?.isWaitingForInsurance;
          }
        })
      )
      .subscribe();

    this.periods$
      .pipe(
        map((subscribePeriods: SubscriptionPeriod[]) => {
          if (subscribePeriods) {
            this.subscriptionPeriods = subscribePeriods?.filter(
              (subscribePeriod) =>
                subscribePeriod.year > new Date().getFullYear() - 1
            );
            this.hasSubscriptionFormEditable = !this.hasSubscriptionFormEditable
              ? subscribePeriods.some((x) => x.editable === true)
              : this.hasSubscriptionFormEditable;
          }
        })
      )
      .subscribe();

    this.userCanEditForm$.pipe(takeUntil(this.destroy$)).subscribe((p) => {
      this.userCanEditForm = p;
    });
  }

  fillForm(period: SubscriptionPeriod) {
    if (period?.subscriptionNoticeId) {
      this.router.navigateByUrl(
        "/farp-subscription/notice/" + period?.subscriptionNoticeId
      );
    } else {
      this.router.navigateByUrl("/farp-subscription/form/" + period?.year);
    }
  }

  redirectToSubscriptionPageIfNeeded() {
    if (this.isAuthorized && this.inactiveMemberForSubscription) {
      this.router.navigate(["/farp-subscription/index"]);
    }
  }

  showReportParticularConditions(period: SubscriptionPeriod) {
    this.store.dispatch(
      new SeeParticularConditions(
        this.memberId,
        period.year,
        this.user.permitNumber
      )
    );
  }

  showReceipt(receipt: SubscriptionReceipt) {
    this.store.dispatch(
      new SeeSubscriptionReceipt(
        this.memberId,
        receipt.id,
        receipt.description,
        this.user.permitNumber
      )
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
